// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

/**
 * Session Timeout Context
 *
 * HIPAA Compliance: 164.312(a)(2)(iii) - Automatic Logoff
 *
 * This context provides session timeout functionality to automatically log out
 * users after a period of inactivity. This is a critical HIPAA requirement to
 * prevent unauthorized access to PHI on unattended workstations.
 *
 * Features:
 * - Configurable timeout duration (default: 15 minutes)
 * - Warning dialog before logout
 * - Activity detection (mouse, keyboard, touch, scroll)
 * - Countdown timer display
 * - Integration with organization settings
 */

import { Button, Group, Modal, Progress, Stack, Text, Title } from '@mantine/core';
import { useMedplum } from '@medplum/react';
import { IconAlertTriangle } from '@tabler/icons-react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { JSX, ReactNode } from 'react';
import { clearSessionStorage } from '../utils/secureLogout';

// Default timeout values (in milliseconds)
const DEFAULT_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes
const WARNING_BEFORE_LOGOUT_MS = 2 * 60 * 1000; // Show warning 2 minutes before logout
const MIN_TIMEOUT_MS = 5 * 60 * 1000; // Minimum 5 minutes
const ACTIVITY_THROTTLE_MS = 1000; // Throttle activity detection to 1 second

// Events that indicate user activity
const ACTIVITY_EVENTS = [
  'mousedown',
  'mousemove',
  'keydown',
  'keypress',
  'touchstart',
  'scroll',
  'wheel',
  'click',
  'input',      // Typing in form fields
  'change',     // Form field value changes (selects, checkboxes, etc.)
  'focus',      // Focusing on form elements
  'pointerdown', // Modern pointer events
] as const;

interface SessionTimeoutContextValue {
  /** Whether session timeout is enabled */
  isEnabled: boolean;
  /** Current timeout setting in minutes */
  timeoutMinutes: number;
  /** Time remaining until logout (in seconds) */
  remainingSeconds: number;
  /** Whether the warning modal is showing */
  isWarningVisible: boolean;
  /** Manually reset the activity timer */
  resetTimer: () => void;
  /** Extend the session (dismiss warning and reset timer) */
  extendSession: () => void;
  /** Manually trigger logout */
  logout: () => void;
  /** Update timeout setting (in minutes, 0 = disabled) */
  setTimeoutMinutes: (minutes: number) => void;
}

const SessionTimeoutContext = createContext<SessionTimeoutContextValue | undefined>(undefined);

interface SessionTimeoutProviderProps {
  children: ReactNode;
  /** Initial timeout in minutes (0 = disabled) */
  initialTimeoutMinutes?: number;
  /** Callback when session expires */
  onSessionExpired?: () => void;
}

export function SessionTimeoutProvider({
  children,
  initialTimeoutMinutes = 15,
  onSessionExpired,
}: SessionTimeoutProviderProps): JSX.Element {
  const medplum = useMedplum();
  const [timeoutMinutes, setTimeoutMinutes] = useState(initialTimeoutMinutes);
  const [remainingSeconds, setRemainingSeconds] = useState(initialTimeoutMinutes * 60);
  const [isWarningVisible, setIsWarningVisible] = useState(false);
  const [isEnabled, setIsEnabled] = useState(initialTimeoutMinutes > 0);

  const lastActivityRef = useRef<number>(Date.now());
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  const activityThrottleRef = useRef<number>(0);
  const isWarningVisibleRef = useRef<boolean>(false);
  const timeoutMsRef = useRef<number>(initialTimeoutMinutes * 60 * 1000);
  const logoutRef = useRef<(() => void) | null>(null);

  // Keep refs in sync with state for use in event handlers (avoids stale closures)
  useEffect(() => {
    isWarningVisibleRef.current = isWarningVisible;
  }, [isWarningVisible]);

  useEffect(() => {
    timeoutMsRef.current = timeoutMinutes * 60 * 1000;
  }, [timeoutMinutes]);

  // Calculate timeout in milliseconds
  const timeoutMs = useMemo(() => {
    if (timeoutMinutes <= 0) return 0;
    return Math.max(timeoutMinutes * 60 * 1000, MIN_TIMEOUT_MS);
  }, [timeoutMinutes]);

  // Logout function
  const logout = useCallback(() => {
    // Clear any pending timers
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }

    // Clear all session and project-specific storage (PHI protection)
    // This clears sessionStorage (drafts), project-specific localStorage,
    // and any Medplum SDK tokens
    clearSessionStorage();

    // Call custom callback if provided
    if (onSessionExpired) {
      onSessionExpired();
    }

    // Sign out from Medplum (belt and suspenders - clearSessionStorage also clears tokens)
    medplum.signOut();

    // Redirect to login
    window.location.href = '/';
  }, [medplum, onSessionExpired]);

  // Keep logout ref in sync (avoids effect re-runs when logout changes)
  useEffect(() => {
    logoutRef.current = logout;
  }, [logout]);

  // Reset the activity timer
  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    setIsWarningVisible(false);
    setRemainingSeconds(timeoutMinutes * 60);
  }, [timeoutMinutes]);

  // Extend session (user clicked "Stay Logged In")
  const extendSession = useCallback(() => {
    resetTimer();
  }, [resetTimer]);

  // Handle user activity - uses refs to avoid stale closures and prevent re-registering event listeners
  const handleActivity = useCallback(() => {
    const now = Date.now();

    // Throttle activity detection to avoid excessive updates
    if (now - activityThrottleRef.current < ACTIVITY_THROTTLE_MS) {
      return;
    }
    activityThrottleRef.current = now;

    // Only reset if warning is not visible (use ref to avoid stale closure)
    if (!isWarningVisibleRef.current) {
      lastActivityRef.current = now;
    }
  }, []); // No dependencies - uses refs for current values

  // Update timeout setting
  const updateTimeoutMinutes = useCallback((minutes: number) => {
    setTimeoutMinutes(minutes);
    setIsEnabled(minutes > 0);
    if (minutes > 0) {
      setRemainingSeconds(minutes * 60);
    }
  }, []);

  // Set up activity event listeners
  useEffect(() => {
    if (!isEnabled) return;

    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [isEnabled, handleActivity]);

  // Main timeout check loop - uses refs for values that may change to avoid effect restarts
  useEffect(() => {
    if (!isEnabled || timeoutMs <= 0) {
      return;
    }

    // Reset lastActivity when effect starts to prevent immediate logout on re-mount
    lastActivityRef.current = Date.now();

    const checkTimeout = (): void => {
      const now = Date.now();
      const elapsed = now - lastActivityRef.current;
      // Use ref for timeoutMs to get current value without restarting effect
      const currentTimeoutMs = timeoutMsRef.current;
      const remaining = Math.max(0, currentTimeoutMs - elapsed);
      const remainingSec = Math.ceil(remaining / 1000);

      setRemainingSeconds(remainingSec);

      // Show warning when approaching timeout
      if (remaining <= WARNING_BEFORE_LOGOUT_MS && remaining > 0) {
        setIsWarningVisible(true);
      }

      // Trigger logout when time is up (use ref to get latest logout function)
      if (remaining <= 0 && logoutRef.current) {
        logoutRef.current();
      }
    };

    // Check every second
    countdownRef.current = setInterval(checkTimeout, 1000);

    // Initial check
    checkTimeout();

    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
    };
  }, [isEnabled, timeoutMs]); // Removed logout from dependencies - using ref instead

  const value = useMemo(
    (): SessionTimeoutContextValue => ({
      isEnabled,
      timeoutMinutes,
      remainingSeconds,
      isWarningVisible,
      resetTimer,
      extendSession,
      logout,
      setTimeoutMinutes: updateTimeoutMinutes,
    }),
    [
      isEnabled,
      timeoutMinutes,
      remainingSeconds,
      isWarningVisible,
      resetTimer,
      extendSession,
      logout,
      updateTimeoutMinutes,
    ]
  );

  // Calculate progress for the warning modal
  const warningProgress = useMemo(() => {
    const warningSeconds = WARNING_BEFORE_LOGOUT_MS / 1000;
    return Math.max(0, Math.min(100, (remainingSeconds / warningSeconds) * 100));
  }, [remainingSeconds]);

  return (
    <SessionTimeoutContext.Provider value={value}>
      {children}

      {/* Session Timeout Warning Modal */}
      <Modal
        opened={isWarningVisible}
        onClose={extendSession}
        title={
          <Group gap="xs">
            <IconAlertTriangle size={24} color="var(--mantine-color-orange-6)" />
            <Title order={4}>Session Expiring Soon</Title>
          </Group>
        }
        centered
        closeOnClickOutside={false}
        closeOnEscape={false}
        withCloseButton={false}
      >
        <Stack gap="md">
          <Text>
            Your session will expire in{' '}
            <Text span fw={700} c="orange">
              {Math.floor(remainingSeconds / 60)}:{String(remainingSeconds % 60).padStart(2, '0')}
            </Text>{' '}
            due to inactivity.
          </Text>

          <Text size="sm" c="dimmed">
            For your security and HIPAA compliance, you will be automatically logged out to protect
            patient information.
          </Text>

          <Progress value={warningProgress} color="orange" size="lg" animated />

          <Group justify="flex-end" mt="md">
            <Button variant="outline" color="red" onClick={logout}>
              Log Out Now
            </Button>
            <Button color="teal" onClick={extendSession}>
              Stay Logged In
            </Button>
          </Group>
        </Stack>
      </Modal>
    </SessionTimeoutContext.Provider>
  );
}

export function useSessionTimeout(): SessionTimeoutContextValue {
  const context = useContext(SessionTimeoutContext);
  if (!context) {
    throw new Error('useSessionTimeout must be used within a SessionTimeoutProvider');
  }
  return context;
}

/**
 * Hook to sync session timeout with organization app settings
 */
export function useSessionTimeoutFromSettings(autoLogoutMinutes: string | number | undefined): void {
  const { setTimeoutMinutes } = useSessionTimeout();

  useEffect(() => {
    if (autoLogoutMinutes !== undefined) {
      const minutes =
        typeof autoLogoutMinutes === 'string' ? parseInt(autoLogoutMinutes, 10) : autoLogoutMinutes;

      if (!isNaN(minutes)) {
        setTimeoutMinutes(minutes);
      }
    }
  }, [autoLogoutMinutes, setTimeoutMinutes]);
}
